import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  selectAnalyticalVariables,
  selectCriteria,
  useAnalyticalVariableListQuery,
  useCohortDefinitionStore,
  type AnalyticalVariableListItem,
} from '@verantos-dev/feature-analytical-variables';

import type { DependentCriteriaMode } from '../../utils/avLibraryEligibility';
import { getSourceLibraryAvIdFromCriterion } from '../../utils/avLibraryUtils';

/**
 * Shared data + search state for AV library modals (Add Variable, Set Index Date).
 * Owns the list query, the in-cohort id set, and the search input;
 * exposes a `filterBySearch` helper so consumers can apply the query to
 * any base list (raw items or an eligibility-narrowed subset).
 */
export const useAvLibraryItems = () => {
  const { projectId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: listResponse,
    isLoading,
    isFetching,
    isError,
  } = useAnalyticalVariableListQuery(undefined, projectId);
  // AVs are dual-shape going forward: each library item has either a
  // structured `jsonValue` (logic / window / aggregation / output) or a
  // `jsonValue: null` with an authoritative `sql_body` server-side. Both are
  // first-class — surface BOTH shapes here. Consumers must branch on shape,
  // not filter SQL-only items out.
  const allItems: AnalyticalVariableListItem[] = listResponse?.data ?? [];

  const criteria = useCohortDefinitionStore(selectCriteria);
  const cohortAnalyticalVariables = useCohortDefinitionStore(selectAnalyticalVariables);

  const inCohortAvIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of criteria) {
      const id = getSourceLibraryAvIdFromCriterion(c);
      if (id) ids.add(id);
    }
    return ids;
  }, [criteria]);

  // The cohort's index rule; 'all' makes the index conditional on the other criteria.
  const dependentCriteriaMode = useMemo((): DependentCriteriaMode => {
    for (const c of criteria) {
      if (
        'criteriaType' in c &&
        c.criteriaType === 'index_date' &&
        c.value.type === 'Analytical variable'
      ) {
        return c.value.dateResolution.dependentCriteriaMode;
      }
    }
    return undefined;
  }, [criteria]);

  /**
   * Library AV ids whose `name` matches an AV already in the cohort but whose
   * `analyticalVariableId` is NOT linked back to the library
   * (i.e., not in `inCohortAvIds`). Use this to flag name collisions where
   * adding the library AV would duplicate an existing cohort variable name.
   */
  const nameConflictAvIds = useMemo(() => {
    const cohortNames = new Set((cohortAnalyticalVariables ?? []).map((av) => av.name));
    const conflicts = new Set<string>();
    for (const item of allItems) {
      if (!inCohortAvIds.has(item.analyticalVariableId) && cohortNames.has(item.name)) {
        conflicts.add(item.analyticalVariableId);
      }
    }
    return conflicts;
  }, [cohortAnalyticalVariables, allItems, inCohortAvIds]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const filterBySearch = useCallback(
    (items: AnalyticalVariableListItem[]) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return items;
      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(q) || (item.description ?? '').toLowerCase().includes(q)
      );
    },
    [searchQuery]
  );

  return {
    allItems,
    isLoading: isLoading || isFetching,
    isError,
    inCohortAvIds,
    nameConflictAvIds,
    dependentCriteriaMode,
    searchQuery,
    handleSearchChange,
    filterBySearch,
  };
};
